import React, { useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { passageCardTop, passageCardView } from './passageCardPlacement.utils';
import { BrandPresencePinnedAreaContext } from '../useHostViewport';

/** The rendered element of one passage inside the answer's grid. */
export function findPassageElement(grid: HTMLElement, unitId: string): HTMLElement | undefined {
  return Array.from(grid.querySelectorAll<HTMLElement>('[data-passage-id]')).find(
    (element) => element.getAttribute('data-passage-id') === unitId
  );
}

/**
 * How the card sits: beside the answer, laid over the sidebar's column at a measured place; or, where
 * the columns stack, in the answer's own flow.
 */
type CardLayout = { mode: 'beside'; top?: number; left?: number; width?: number; pointer?: number } | { mode: 'stacked' };

/** How far the card's pointer sits from the card's own top and bottom edge, at the least. */
const POINTER_EDGE_INSET = 16;

/** Half the pointer's size (`h-2.5`), so its tip sits at the measured offset. */
const POINTER_HALF_SIZE = 5;

/** How far below the passage's top the pointer aims: the middle of its first line. */
const POINTER_FIRST_LINE_OFFSET = 12;

/**
 * Where along the card's height its pointer sits: level with the first line of the passage, and
 * never so close to a corner that it would leave the card.
 */
function pointerOffset({
  passageTop,
  passageBottom,
  cardTop,
  cardHeight,
}: {
  passageTop: number;
  passageBottom: number;
  cardTop: number;
  cardHeight: number;
}): number {
  const aim = passageTop + Math.min(POINTER_FIRST_LINE_OFFSET, (passageBottom - passageTop) / 2) - cardTop;
  return Math.round(Math.max(POINTER_EDGE_INSET, Math.min(aim, cardHeight - POINTER_EDGE_INSET)));
}

const UNPLACED: CardLayout = { mode: 'beside' };

function sameLayout(left: CardLayout, right: CardLayout): boolean {
  if (left.mode === 'stacked' || right.mode === 'stacked') {
    return left.mode === right.mode;
  }
  return left.top === right.top && left.left === right.left && left.width === right.width && left.pointer === right.pointer;
}

/**
 * The passage card, rendered right after the answer so the keyboard reaches it before the sidebar.
 *
 * Beside the answer it is laid over the sidebar's column — positioned against the answer's grid, so
 * it takes no room there and the sidebar keeps its place — level with the passage being pointed at,
 * else the pinned one, and placed again whenever the page scrolls, the window resizes, or the grid or
 * the card changes size, so it follows the passage. A preview lets the pointer through to the sidebar
 * under it; a pinned card takes clicks, so its sources can be opened.
 *
 * Where the columns stack there is no room beside the text: only a pinned passage's card shows, in the
 * flow after the answer, where it covers nothing and moves nothing above the reader's place.
 */
export function PassageCardLayer({
  gridRef,
  sidebarRef,
  previewId,
  pinnedId,
  renderCard,
}: {
  /** The answer's grid; it must be positioned, so the card's offsets are measured from it. */
  gridRef: React.RefObject<HTMLElement | null>;
  /** The sidebar, whose column the card lies over and whose place tells whether the columns stack. */
  sidebarRef: React.RefObject<HTMLElement | null>;
  /** The passage being pointed at, if any. */
  previewId?: string;
  pinnedId?: string;
  renderCard: (passageId: string) => React.ReactNode;
}): React.ReactElement {
  const layerRef = useRef<HTMLDivElement>(null);
  const [layout, setLayout] = useState<CardLayout>(UNPLACED);
  // Where the host page scrolls the frame, the host's reports move the visible page, and each one
  // renders this layer again, which places the card.
  const { hostScrollsFrame, hostTop, hostHeight, pinnedHeight } = useContext(BrandPresencePinnedAreaContext);
  const shownId = layout.mode === 'beside' ? (previewId ?? pinnedId) : pinnedId;

  const place = useCallback(() => {
    const grid = gridRef.current;
    const sidebar = sidebarRef.current;
    const layer = layerRef.current;
    if (!grid || !sidebar || !layer) {
      return;
    }
    const gridBox = grid.getBoundingClientRect();
    const sidebarBox = sidebar.getBoundingClientRect();
    // The columns stack when the sidebar starts where the grid does. A page with no layout to read
    // counts as side by side, the layout the card is designed for.
    const next = (): CardLayout => {
      if (sidebarBox.width > 0 && sidebarBox.left <= gridBox.left) {
        return { mode: 'stacked' };
      }
      const unitId = previewId ?? pinnedId;
      const passage = unitId ? findPassageElement(grid, unitId) : undefined;
      if (!passage) {
        return UNPLACED;
      }
      const passageBox = passage.getBoundingClientRect();
      const passageTop = passageBox.top - gridBox.top;
      const passageBottom = passageBox.bottom - gridBox.top;
      const cardHeight = layer.getBoundingClientRect().height;
      const top = passageCardTop({
        passageTop,
        passageBottom,
        cardHeight,
        gridHeight: gridBox.height,
        ...passageCardView({
          gridTop: gridBox.top,
          windowHeight: window.innerHeight,
          host: hostScrollsFrame ? { top: hostTop, height: hostHeight } : undefined,
          pinnedHeight,
        }),
      });
      return {
        mode: 'beside',
        top,
        left: Math.round(sidebarBox.left - gridBox.left),
        width: Math.round(sidebarBox.width),
        pointer: pointerOffset({ passageTop, passageBottom, cardTop: top, cardHeight }),
      };
    };
    const placed = next();
    setLayout((current) => (sameLayout(current, placed) ? current : placed));
  }, [gridRef, sidebarRef, previewId, pinnedId, hostScrollsFrame, hostTop, hostHeight, pinnedHeight]);

  // Placed before the browser paints, after every render: the passage, the pin or the card's content
  // may have changed its height.
  useLayoutEffect(place);

  useEffect(() => {
    let frame = 0;
    const schedule = () => {
      if (!frame) {
        frame = window.requestAnimationFrame(() => {
          frame = 0;
          place();
        });
      }
    };
    // Captured, because what scrolls may be any element around the page, not the window itself.
    window.addEventListener('scroll', schedule, { capture: true, passive: true });
    window.addEventListener('resize', schedule);
    const observer = typeof ResizeObserver === 'undefined' ? undefined : new ResizeObserver(schedule);
    for (const element of [gridRef.current, layerRef.current]) {
      if (observer && element) {
        observer.observe(element);
      }
    }
    return () => {
      window.removeEventListener('scroll', schedule, { capture: true });
      window.removeEventListener('resize', schedule);
      observer?.disconnect();
      if (frame) {
        window.cancelAnimationFrame(frame);
      }
    };
  }, [gridRef, place]);

  const card = shownId ? renderCard(shownId) : null;

  if (layout.mode === 'stacked') {
    return (
      <div ref={layerRef} data-passage-card-for={shownId} aria-live="polite" className={card ? 'mt-3' : undefined}>
        {card}
      </div>
    );
  }

  const pinned = !!shownId && shownId === pinnedId;
  const waitingForPlacement = !!card && layout.top === undefined;
  const coversSidebar = !!card && !waitingForPlacement;
  return (
    <div
      ref={layerRef}
      data-passage-card-for={shownId}
      // Lets the sidebar under the card step back while the card is shown over it.
      {...(coversSidebar ? { 'data-covers-sidebar': '' } : {})}
      aria-live="polite"
      // Held out of sight only while a card waits for its first placement; an empty region stays exposed.
      className={['absolute z-20', pinned ? 'pointer-events-auto' : 'pointer-events-none', waitingForPlacement ? 'invisible' : undefined]
        .filter(Boolean)
        .join(' ')}
      style={{ top: layout.top ?? 0, left: layout.left, width: layout.width }}
    >
      {card}
      {card && layout.pointer !== undefined ? (
        // A small notch on the card's edge that points across the gap at the passage; drawn after the
        // card so it covers the card's ring where they meet. Its offset is measured at runtime.
        <span
          aria-hidden
          className="pointer-events-none absolute -left-[5px] h-2.5 w-2.5 rotate-45 rounded-bl-[2px] border-b border-l border-slate-900/10 bg-white"
          style={{ top: layout.pointer - POINTER_HALF_SIZE }}
        />
      ) : null}
    </div>
  );
}
